/**
 * InfinitSales - leitura do JWT no cliente (sem biblioteca)
 *
 * Só decodifica o payload (base64url) para ler `exp`. NÃO valida assinatura —
 * quem decide se o token vale é o backend; aqui apenas evitamos bater no
 * servidor com um token que já sabemos vencido (laço de 401 no SSE).
 *
 * Mantido puro (sem importar api/authService) para poder ser usado pelo
 * sseService e pelo AuthContext sem dependência circular.
 */

/** Margem antes do `exp` a partir da qual tratamos o token como vencido. */
export const JWT_EXP_MARGIN_MS = 30_000;

/**
 * Decodifica o payload de um JWT. Devolve null para qualquer entrada inválida.
 * @param {string} token
 * @returns {object|null}
 */
export function decodeJwtPayload(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const bin = atob(padded);
    // UTF-8 sem TextDecoder (o jsdom dos testes não o expõe).
    const json = decodeURIComponent(
      Array.from(bin, (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
    );
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

/**
 * Instante de expiração do token em ms (epoch), ou null se o token não tem
 * `exp` legível. Token sem `exp` NÃO é tratado como vencido.
 * @param {string} token
 * @returns {number|null}
 */
export function getJwtExpMs(token) {
  const payload = decodeJwtPayload(token);
  const exp = Number(payload?.exp);
  return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null;
}

/**
 * true se o token tem `exp` e ele já passou (ou passa dentro da margem).
 * @param {string} token
 * @param {number} [marginMs]
 * @param {number} [nowMs] - injetável para testes
 */
export function isJwtExpired(token, marginMs = JWT_EXP_MARGIN_MS, nowMs = Date.now()) {
  const expMs = getJwtExpMs(token);
  if (expMs == null) return false;
  return nowMs + marginMs >= expMs;
}
